/**
 * "Text it to your captains" helpers (INT-0028, wizard finish screen).
 *
 * Staty never sends the message itself: there are no real recipients at
 * wizard end, so the organizer's own share sheet / Messages / mail client
 * carries it, from their own number or address. Pure functions, no React.
 */

export interface ShareMessageInput {
  orgName: string;
  url: string;
  /** Individual sport (ADR 0049): "Register here" instead of "your team". */
  individual?: boolean;
}

/** The prefilled message. No em dashes (owner 2026-10-12). */
export function buildShareMessage({ orgName, url, individual }: ShareMessageInput): string {
  const name = String(orgName || 'our league').trim();
  return individual
    ? `Signup for ${name} is open. Register here: ${url}`
    : `Signup for ${name} is open. Register your team here: ${url}`;
}

export function buildShareSubject(orgName: string): string {
  return `${String(orgName || 'Our league').trim()} signup is open`;
}

/**
 * The schedule-first variant (INT-0043): when the organizer pasted their
 * teams in the wizard, the artifact is the public schedule page, not the
 * registration link. No em dashes.
 */
export function buildScheduleShareMessage({ orgName, url }: { orgName: string; url: string }): string {
  return `The ${String(orgName || 'league').trim()} schedule is up: ${url}`;
}

export function buildScheduleShareSubject(orgName: string): string {
  return `${String(orgName || 'Our league').trim()} schedule is up`;
}

export function isTouchDevice(): boolean {
  try {
    // maxTouchPoints + a phone/tablet UA. NOT `'ontouchstart' in window`:
    // desktop Chrome (and jsdom) define that handler slot regardless.
    return (navigator.maxTouchPoints || 0) > 0 || /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || '');
  } catch {
    return false;
  }
}

export function isIOS(): boolean {
  try {
    const ua = navigator.userAgent || '';
    return /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && (navigator.maxTouchPoints || 0) > 1);
  } catch {
    return false;
  }
}

/**
 * `sms:` URI with a prefilled body. iOS reads `&body=` (and chokes on `?`),
 * Android and the rest read `?body=`.
 */
export function smsHref(body: string): string {
  const enc = encodeURIComponent(body);
  return isIOS() ? `sms:&body=${enc}` : `sms:?body=${enc}`;
}

export function mailtoHref(subject: string, body: string): string {
  return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

/** Where the share button points when the Web Share API is unavailable. */
export function fallbackShareHref(subject: string, body: string): string {
  return isTouchDevice() ? smsHref(body) : mailtoHref(subject, body);
}

export function canNativeShare(): boolean {
  try {
    return typeof navigator !== 'undefined' && typeof (navigator as any).share === 'function';
  } catch {
    return false;
  }
}

export type NativeShareResult = 'shared' | 'cancelled' | 'failed';

/** Web Share API. A dismissed sheet is `cancelled`, never an error. */
export async function nativeShare(data: { title: string; text: string; url: string }): Promise<NativeShareResult> {
  try {
    await (navigator as any).share(data);
    return 'shared';
  } catch (err: any) {
    if (err && err.name === 'AbortError') return 'cancelled';
    return 'failed';
  }
}

/** Clipboard write with a textarea fallback for insecure contexts. */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through to the legacy path */
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}
